// The /black-friday landing page is live all year. These helpers turn the
// sales from /api/sale_specials/seasons/:season (this year's, running or
// coming up, and last year's) into what the page says.

// Sales start and end at midnight Pacific (backstage stores 08:00 / 07:59 UTC,
// and bar swaps the sale prices in on the same clock), so dates are shown in
// Pacific time: a sale ending 07:59 UTC on December 1 ends on November 30.
const SALE_TIME_ZONE = "America/Los_Angeles"

// From the October after a sale, the page stops saying it has ended and
// starts saying it's coming, whether or not backstage has the new one yet.
const NEXT_SEASON_MONTH = 10

const toDate = (value) => {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

const datePart = (value, options) =>
  new Intl.DateTimeFormat("en-US", {
    ...options,
    timeZone: SALE_TIME_ZONE,
  }).format(toDate(value))

export const saleYear = (value) => Number(datePart(value, { year: "numeric" }))

// 11 for November
export const saleMonth = (value) =>
  Number(datePart(value, { month: "numeric" }))

// "Friday, November 21"
export const saleDate = (value) =>
  datePart(value, { weekday: "long", month: "long", day: "numeric" })

// "November 21"
export const shortSaleDate = (value) =>
  datePart(value, { month: "long", day: "numeric" })

// "NOV 21"
export const tinySaleDate = (value) =>
  datePart(value, { month: "short", day: "numeric" }).toUpperCase()

// "Friday"
export const saleWeekday = (value) => datePart(value, { weekday: "long" })

// 21
export const saleDayOfMonth = (value) =>
  Number(datePart(value, { day: "numeric" }))

// "11:59 pm PT"
export const saleTime = (value) =>
  `${datePart(value, { hour: "numeric", minute: "2-digit" }).toLowerCase()} PT`

// The day a sale ends, as the copy says it: "Cyber Monday" when it ends on
// a Monday in November or December, as Black Friday sales do, or the date.
export const saleEndDay = (value) => {
  if (!value) return "Cyber Monday"
  return saleWeekday(value) === "Monday" && saleMonth(value) >= 11
    ? "Cyber Monday"
    : saleDate(value)
}

// "Cyber Monday, November 30", or "Tuesday, December 1" for a sale that
// ends on another day.
export const saleEndDate = (value) =>
  saleEndDay(value) === "Cyber Monday"
    ? `Cyber Monday, ${shortSaleDate(value)}`
    : saleDate(value)

// Days, hours and minutes from `now` to `end`: "3D 14H 22M".
export const countdown = (end, now = new Date()) => {
  const minutes = Math.max(0, Math.floor((toDate(end) - now) / 60000))
  const days = Math.floor(minutes / 1440)
  const hours = Math.floor((minutes % 1440) / 60)
  return `${days}D ${hours}H ${minutes % 60}M`
}

// Where the season stands at `now`:
//   live        — `sale` is running
//   upcoming    — `sale` is scheduled and hasn't started
//   ended       — the last sale is over, and it's not October yet
//   unscheduled — nothing set up yet, but a sale is coming this November
// `year` is the year the page is about: the sale's own, the one that ended,
// or this year's.
export const seasonState = ({ sale, previous }, now = new Date()) => {
  const startsAt = toDate(sale?.startAt)
  const endsAt = toDate(sale?.endAt)

  if (startsAt && endsAt && endsAt >= now) {
    return {
      phase: startsAt <= now ? "live" : "upcoming",
      year: saleYear(startsAt),
      startsAt,
      endsAt,
    }
  }

  const lastEnd = endsAt ?? toDate(previous?.endAt)
  if (lastEnd) {
    const ended = saleYear(lastEnd)
    const thisYear = saleYear(now)
    if (
      thisYear === ended ||
      (thisYear === ended + 1 && saleMonth(now) < NEXT_SEASON_MONTH)
    ) {
      return { phase: "ended", year: ended, startsAt: null, endsAt: null }
    }
  }

  return {
    phase: "unscheduled",
    year: saleYear(now),
    startsAt: null,
    endsAt: null,
  }
}

// Outside production, ?preview=live|upcoming|unscheduled|ended shows the page
// in that phase whatever backstage has scheduled. The dates are made up
// around `now`.
const DAY = 24 * 60 * 60 * 1000

export const previewSeason = (season, preview, now = new Date()) => {
  const fake = (startOffset, endOffset) => ({
    name: "Black Friday SALE (preview)",
    startAt: new Date(now.getTime() + startOffset * DAY).toISOString(),
    endAt: new Date(now.getTime() + endOffset * DAY).toISOString(),
  })

  switch (preview) {
    case "live":
      return { sale: fake(-2, 4), previous: season.previous }
    case "upcoming":
      return { sale: fake(30, 36), previous: season.previous }
    case "unscheduled":
      return { sale: null, previous: null }
    case "ended":
      return { sale: null, previous: fake(-12, -6) }
    default:
      return season
  }
}
